<template>
  <section id="proctoring" aria-labelledby="proctoring-heading" class="py-20 px-6 bg-white">
    <div class="max-w-[1300px] mx-auto">
      <!-- Heading -->
      <header class="text-center max-w-3xl mx-auto mb-12">
        <span class="inline-block bg-blue-100 text-[#1E3A5F] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
          Examination Integrity
        </span>
        <h2 id="proctoring-heading" class="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
          AI Proctoring Suite
        </h2>
        <p class="mt-4 text-lg text-gray-600">
          Conduct secure, intelligent, and scalable online examinations with our advanced AI-powered proctoring solution — designed for schools, colleges, universities, and professional examination bodies.
        </p>
      </header>

      <!-- Core features grid -->
      <ul class="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-20 list-none m-0 p-0">
        <li
          v-for="feature in coreFeatures"
          :key="feature"
          class="flex items-start gap-3 bg-blue-50/40 border border-blue-100 rounded-xl px-4 py-3"
        >
          <i class="pi pi-shield text-[#1A56DB] text-lg mt-0.5" aria-hidden="true"></i>
          <span class="text-sm text-gray-800 font-medium">{{ feature }}</span>
        </li>
      </ul>

      <!-- Pricing lives on the dedicated Pricing page -->
      <div class="text-center mb-16">
        <router-link
          :to="{ name: 'landing-pricing', hash: '#proctoring-pricing' }"
          class="inline-flex items-center justify-center gap-2 bg-[#1A56DB] hover:bg-[#2563EB] text-white font-semibold px-6 py-3 rounded-md shadow-md transition no-underline"
          data-testid="proctoring-see-pricing-link"
        >
          See AI Proctoring plans &amp; pricing
          <i class="pi pi-arrow-right" aria-hidden="true"></i>
        </router-link>
      </div>

      <!-- Contact CTA -->
      <div class="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E3A5F] via-[#1A56DB] to-[#1E3A5F] px-8 py-10 lg:p-14 text-center">
        <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,255,255,0.15),transparent_50%)]" aria-hidden="true"></div>
        <div class="relative max-w-2xl mx-auto">
          <h3 class="text-2xl md:text-3xl font-bold text-white leading-tight">
            Get in touch with our team
          </h3>
          <p class="mt-3 text-blue-50">
            We'll walk you through deployment, onboarding, and tailored pricing for your institution.
          </p>
          <template v-if="!isWhiteLabel">
            <button
              type="button"
              class="mt-6 inline-flex items-center justify-center gap-2 bg-white text-[#1E3A5F] hover:bg-gray-100 font-semibold px-6 py-3 rounded-md shadow-md transition border-0 cursor-pointer"
              data-testid="proctoring-contact-btn"
              @click="landingStore.openDemo(DemoRequestKind.Sales)"
            >
              <i class="pi pi-comments" aria-hidden="true"></i>
              Talk to our team
            </button>
            <p class="mt-3 text-sm text-blue-100">
              Or email
              <a href="mailto:support@gradeverse.com" class="font-semibold text-white hover:underline">support@gradeverse.com</a>.
            </p>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useBranding } from '@/composables/useBranding';
import { useLandingStore } from '@/store/landingStore';
import { DemoRequestKind } from '@/types/composition-functions';

const { isWhiteLabel } = useBranding();
const landingStore = useLandingStore();

const coreFeatures = [
  'Suspicious activity detection',
  'Webcam and screen monitoring',
  'Real-time invigilator dashboard',
  'Examination analytics and reports',
  'Institution branding support',
  'Scalable for large examinations',
  'Local deployment and technical support',
];
</script>
